import { ApiAgentRequest, request } from '../utils'
import { useAuthMutation } from './auth'
import { CreateLanguageProficiencyRequestAttributes } from '@backend/agents-ui-api/resources/student-document/language-proficiency/types'
import { CreateLanguageProficiencyRequest, CreateLanguageProficiencyResponse } from "@backend/agents-ui-api/routes/students/{studentId}/language-proficiencies/POST/types"
import { useS3Upload } from './useS3Upload'
import { useQueryClient } from '@tanstack/react-query'

type UploadLanguageProficiencyParams = CreateLanguageProficiencyRequest['data'] & {
  file?: File
}

async function CreateLanguageProficiency(studentId: string, data: CreateLanguageProficiencyRequestAttributes) {
  const response = await request<CreateLanguageProficiencyResponse>(
    new ApiAgentRequest(`/students/${studentId}/language-proficiencies`, {
      method: 'POST',
      body: {
        data: data,
      },
    }),
    {
      isExpectedResponse: (res): res is CreateLanguageProficiencyResponse => res as boolean,
    },
  )

  return response
}

export function useCreateLanguageProficiency() {
  const { s3UploadAsync } = useS3Upload()
  const queryClient = useQueryClient()
  
  const { isPending, mutate, mutateAsync } = useAuthMutation(
    async (params: { studentId: string, data: UploadLanguageProficiencyParams }) => {
      const { file, ...data } = params.data
      const response = await CreateLanguageProficiency(params.studentId, data)
      if (file) {
        const languageProficiencyUpload = response.data.meta.files[params.data.attributes.fileName].upload
      
        await s3UploadAsync({
          file,
          url: languageProficiencyUpload?.url!,
          fields: languageProficiencyUpload?.fields!,
        })
      }
      return response
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate language proficiency queries to refetch the data
        queryClient.invalidateQueries({
          queryKey: ['language-proficiency', variables.studentId]
        })
      }
    }
  )

  return {
    isCreatingLanguageProficiency: isPending,
    createLanguageProficiency: mutate,
    createLanguageProficiencyAsync: mutateAsync,
  }
}


